import React, { useState, useEffect } from 'react';
import { X, Save, MapPin, AlertTriangle, Navigation } from 'lucide-react';

interface Lieu {
  id: string;
  nom: string;
  adresse: string;
  coordonnees: { lat: number; lng: number };
  description: string;
  type: 'domicile' | 'travail' | 'frequentation' | 'autre';
  photos: string[];
}

interface LieuModalProps {
  isOpen: boolean;
  onClose: () => void;
  lieu?: Lieu | null;
  onSave: (lieu: Omit<Lieu, 'id'>) => void;
  mode: 'add' | 'edit';
}

const LieuModal: React.FC<LieuModalProps> = ({ 
  isOpen, 
  onClose, 
  lieu, 
  onSave, 
  mode 
}) => {
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
    coordonnees: { lat: 0, lng: 0 },
    description: '',
    type: 'autre' as 'domicile' | 'travail' | 'frequentation' | 'autre',
    photos: [] as string[]
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (lieu && mode === 'edit') {
      setFormData({
        nom: lieu.nom,
        adresse: lieu.adresse,
        coordonnees: { ...lieu.coordonnees },
        description: lieu.description,
        type: lieu.type,
        photos: [...lieu.photos]
      });
    } else {
      setFormData({
        nom: '',
        adresse: '',
        coordonnees: { lat: 0, lng: 0 },
        description: '',
        type: 'autre',
        photos: []
      });
    }
    setErrors({});
  }, [lieu, mode, isOpen]);

  // Gestion de la touche Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom du lieu est requis';
    }
    
    if (!formData.adresse.trim()) {
      newErrors.adresse = 'L\'adresse est requise';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
      onClose();
    }
  };

  const handleCoordinateChange = (field: 'lat' | 'lng', value: string) => {
    const numValue = parseFloat(value) || 0;
    setFormData(prev => ({
      ...prev,
      coordonnees: {
        ...prev.coordonnees,
        [field]: numValue
      }
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }} onClick={onClose}>
      <div className="w-full max-w-2xl osint-card rounded-lg max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-osint-light-gray">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-osint-orange rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {mode === 'add' ? 'Ajouter un lieu' : 'Modifier le lieu'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nom */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nom du lieu *
              </label>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                className={`osint-input w-full ${errors.nom ? 'border-red-500' : ''}`}
                placeholder="Nom du lieu"
              />
              {errors.nom && (
                <p className="text-red-500 text-sm mt-1 flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  {errors.nom}
                </p>
              )}
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Type de lieu
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                className="osint-input w-full"
              >
                <option value="domicile">Domicile</option>
                <option value="travail">Travail</option>
                <option value="frequentation">Fréquentation</option>
                <option value="autre">Autre</option>
              </select>
            </div>
          </div>

          {/* Adresse */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Adresse *
            </label>
            <input
              type="text"
              value={formData.adresse}
              onChange={(e) => setFormData(prev => ({ ...prev, adresse: e.target.value }))}
              className={`osint-input w-full ${errors.adresse ? 'border-red-500' : ''}`}
              placeholder="123 Rue de la Paix, 75001 Paris"
            />
            {errors.adresse && (
              <p className="text-red-500 text-sm mt-1 flex items-center">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {errors.adresse}
              </p>
            )}
          </div>

          {/* Coordonnées */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Coordonnées GPS
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Latitude</label>
                <input
                  type="number"
                  step="any"
                  value={formData.coordonnees.lat}
                  onChange={(e) => handleCoordinateChange('lat', e.target.value)}
                  className="osint-input w-full"
                  placeholder="48.8566"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Longitude</label>
                <input
                  type="number"
                  step="any"
                  value={formData.coordonnees.lng}
                  onChange={(e) => handleCoordinateChange('lng', e.target.value)}
                  className="osint-input w-full"
                  placeholder="2.3522"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2 mt-2">
              <Navigation className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-400">
                Coordonnées: {formData.coordonnees.lat}, {formData.coordonnees.lng}
              </span>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="osint-input w-full h-24 resize-none"
              placeholder="Description du lieu, son importance dans l'enquête..."
            />
          </div>

          {/* Photos */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Photos associées
            </label>
            <div className="border-2 border-dashed border-osint-light-gray rounded-lg p-6 text-center">
              <div className="flex flex-col items-center space-y-2">
                <div className="w-12 h-12 bg-osint-light-gray rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-400 text-sm">
                  {formData.photos.length > 0 
                    ? `${formData.photos.length} photo(s) associée(s)` 
                    : 'Aucune photo associée'
                  }
                </p>
                <button
                  type="button"
                  className="px-4 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors"
                >
                  Ajouter des photos
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-osint-light-gray flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-osint-light-gray hover:bg-gray-600 rounded-lg text-white text-sm transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex items-center space-x-2 px-4 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white text-sm transition-colors"
            >
              <Save className="w-4 h-4" />
              <span>{mode === 'add' ? 'Ajouter' : 'Modifier'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LieuModal; 